import React, { useEffect, useMemo, useState } from 'react';
import { PageHeader, StatCard, Card, Btn, Input, Select, Modal, Empty, Spinner, Badge, useToast } from '../../components/shared/UI';
import api from '../../utils/api';

const fmtDate = d => d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' }) : '—';
const fmtDateTime = d => d ? new Date(d).toLocaleString(undefined, { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtAgo = d => {
  if (!d) return 'never';
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};
const fmtMoney = (n, cur = 'MYR') =>
  `${cur} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = n => Number(n || 0).toLocaleString();

const PLAN_COLORS   = { monthly: '#10B981', yearly: '#059669', trial: '#F59E0B', cancelled: '#94a3b8' };
const STATUS_COLORS = { active: '#10B981', trialing: '#F59E0B', past_due: '#EF4444', cancelled: '#94a3b8', unpaid: '#EF4444', incomplete: '#8B5CF6' };

const COLUMNS = [
  { key: 'name',           label: 'User',        align: 'left'  },
  { key: 'plan',           label: 'Plan',        align: 'left'  },
  { key: 'documents',      label: 'Docs',        align: 'right' },
  { key: 'clients',        label: 'Clients',     align: 'right' },
  { key: 'items',          label: 'Items',       align: 'right' },
  { key: 'totalValue',     label: 'Total value', align: 'right' },
  { key: 'paidValue',      label: 'Paid',        align: 'right' },
  { key: 'events30d',      label: 'Events 30d',  align: 'right' },
  { key: 'logins',         label: 'Logins',      align: 'right' },
  { key: 'lastActivityAt', label: 'Last seen',   align: 'left'  },
  { key: 'createdAt',      label: 'Joined',      align: 'left'  },
];

const SEGMENTS = [
  { key: 'all',        label: 'All users',        test: () => true },
  { key: 'paying',     label: 'Paying',           test: u => ['monthly', 'yearly'].includes(u.plan) && u.subscriptionStatus === 'active' },
  { key: 'trialing',   label: 'On trial',         test: u => u.subscriptionStatus === 'trialing' },
  { key: 'inactive',   label: 'Deactivated',      test: u => !u.isActive },
  { key: 'unverified', label: 'Unverified email', test: u => !u.emailVerified },
  { key: 'dormant',    label: 'Dormant (30d)',    test: u => !u.events30d },
  { key: 'empty',      label: 'No documents',     test: u => !u.documents },
];

export default function UserStats() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState('');
  const [q, setQ]             = useState('');
  const [segment, setSegment] = useState('all');
  const [sort, setSort]       = useState({ key: 'documents', dir: 'desc' });
  const [detail, setDetail]   = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const { show, ToastContainer } = useToast();

  const load = () => {
    setLoading(true);
    api.get('/admin/user-stats')
      .then(r => { setData(r.data); setErr(''); })
      .catch(e => setErr(e.response?.data?.message || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openDetail = async u => {
    setDetail({ loading: true, id: u.id, name: u.name });
    setDetailLoading(true);
    try {
      const { data: res } = await api.get(`/admin/users/${u.id}/stats`);
      setDetail(res);
    } catch (e) {
      show(e.response?.data?.message || 'Failed to load user statistics', 'error');
      setDetail(null);
    } finally { setDetailLoading(false); }
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const seg = SEGMENTS.find(s => s.key === segment) || SEGMENTS[0];
    const needle = q.trim().toLowerCase();
    const filtered = data.users
      .filter(seg.test)
      .filter(u => !needle || `${u.name} ${u.email} ${u.plan}`.toLowerCase().includes(needle));

    const { key, dir } = sort;
    const mul = dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a[key], bv = b[key];
      if (typeof av === 'string' || typeof bv === 'string') {
        // Dates arrive as ISO strings — lexical order matches chronological order.
        return String(av ?? '').localeCompare(String(bv ?? '')) * mul;
      }
      return ((av ?? 0) - (bv ?? 0)) * mul;
    });
  }, [data, q, segment, sort]);

  const toggleSort = key =>
    setSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });

  const exportCsv = () => {
    const cell = v => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ['Name', 'Email', 'Role', 'Active', 'Verified', 'Plan', 'Status', 'Joined',
                    'Documents', 'Clients', 'Items', 'Currency', 'Total value', 'Paid value', 'Pipeline value',
                    'Events', 'Events 30d', 'Logins', 'Last login', 'Last activity', 'Docs/week'];
    const csv = [header.join(',')].concat(rows.map(u => [
      u.name, u.email, u.role, u.isActive ? 'yes' : 'no', u.emailVerified ? 'yes' : 'no',
      u.plan, u.subscriptionStatus, new Date(u.createdAt).toISOString().slice(0, 10),
      u.documents, u.clients, u.items, u.primaryCurrency, u.totalValue.toFixed(2), u.paidValue.toFixed(2), u.pipelineValue.toFixed(2),
      u.events, u.events30d, u.logins,
      u.lastLoginAt ? new Date(u.lastLoginAt).toISOString() : '',
      u.lastActivityAt ? new Date(u.lastActivityAt).toISOString() : '',
      u.docsPerWeek,
    ].map(cell).join(','))).join('\n');

    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `user-statistics-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    show('CSV exported');
  };

  if (loading && !data) return <Spinner />;
  if (err) return <Card><p style={{ color: 'var(--danger)' }}>{err}</p></Card>;

  const o = data.overview;
  const maxSignup = Math.max(1, ...o.signupsByMonth.map(m => m.count));

  return (
    <div>
      <ToastContainer />
      <PageHeader
        title="User Statistics"
        subtitle={`${o.total} account${o.total !== 1 ? 's' : ''} · generated ${fmtDateTime(data.generatedAt)}`}
        actions={
          <>
            <Btn variant="outline" onClick={load} disabled={loading}>↻ Refresh</Btn>
            <Btn variant="outline" onClick={exportCsv}>⬇ Export CSV</Btn>
          </>
        }
      />

      {/* Headline */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 20 }}>
        <StatCard label="Total users"      value={num(o.total)}         icon="👥" color="#2E75B6" sub={`${o.admins} admin(s)`} />
        <StatCard label="Paying"           value={num(o.paying)}        icon="💎" color="#10B981" sub={`${o.trialing} on trial`} />
        <StatCard label="Active (30d)"     value={num(o.activeLast30d)} icon="⚡" color="#8B5CF6" sub={`${o.total - o.activeLast30d} dormant`} />
        <StatCard label="New (30d)"        value={num(o.new30d)}        icon="🌱" color="#F59E0B" sub={`${o.new7d} in the last 7d`} />
        <StatCard label="Documents"        value={num(o.totalDocuments)} icon="📄" color="#06B6D4" sub={`${o.avgDocsPerUser} avg/user`} />
        <StatCard label="Logged events"    value={num(o.totalEvents)}   icon="📜" color="#EC4899" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 16, marginBottom: 20 }}>
        {/* Account health */}
        <Card>
          <h3 style={cardH}>Account Health</h3>
          <Row k="Active accounts"      v={`${num(o.active)} / ${num(o.total)}`} />
          <Row k="Deactivated"          v={num(o.inactive)} />
          <Row k="Email verified"       v={`${num(o.verified)} (${o.total ? Math.round(o.verified / o.total * 100) : 0}%)`} />
          <Row k="Awaiting verification" v={num(o.unverified)} />
          <Row k="Never signed in"      v={num(o.neverLoggedIn)} />
          <Row k="No documents yet"     v={num(o.withNoDocuments)} />
          <Row k="Churned / unpaid"     v={num(o.churned)} />
        </Card>

        {/* Plans */}
        <Card>
          <h3 style={cardH}>Plans</h3>
          <DistBar rows={o.byPlan} total={o.total} colors={PLAN_COLORS} />
        </Card>

        {/* Subscription status */}
        <Card>
          <h3 style={cardH}>Subscription Status</h3>
          <DistBar rows={o.byStatus} total={o.total} colors={STATUS_COLORS} />
        </Card>

        {/* Content totals */}
        <Card>
          <h3 style={cardH}>Content Created</h3>
          <Row k="Documents" v={num(o.totalDocuments)} />
          <Row k="Clients"   v={num(o.totalClients)} />
          <Row k="Items"     v={num(o.totalItems)} />
          <Row k="Avg documents / user" v={o.avgDocsPerUser} />
          <Row k="New users (24h)" v={num(o.new24h)} />
        </Card>
      </div>

      {/* Signups over time */}
      {o.signupsByMonth.length > 0 && (
        <Card style={{ marginBottom: 20 }}>
          <h3 style={cardH}>Signups by month</h3>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 120 }}>
            {o.signupsByMonth.map(m => (
              <div key={m.month} title={`${m.month}: ${m.count} signup(s)`}
                style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, height: '100%', justifyContent: 'flex-end' }}>
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{m.count}</span>
                <div style={{
                  width: '100%', maxWidth: 40, borderRadius: '4px 4px 0 0',
                  height: `${Math.max(4, (m.count / maxSignup) * 100)}%`,
                  background: 'linear-gradient(180deg, #F59E0B, #D97706)',
                }} />
                <span style={{ fontSize: 9, color: 'var(--text-subtle)', whiteSpace: 'nowrap' }}>{m.month.slice(2)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Per-user table */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <Input label="Search users" placeholder="name, email or plan" value={q} onChange={e => setQ(e.target.value)} />
          <Select label="Segment" value={segment} onChange={e => setSegment(e.target.value)}>
            {SEGMENTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </Select>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 12 }}>
          Showing {rows.length} of {o.total} account(s) · click any row for a full breakdown
        </p>
      </Card>

      {rows.length === 0
        ? <Empty icon="👥" message="No users match this filter" />
        : (
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: 1100, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                    {COLUMNS.map(c => (
                      <th key={c.key} onClick={() => toggleSort(c.key)}
                        style={{ ...th, textAlign: c.align, cursor: 'pointer', color: sort.key === c.key ? 'var(--heading)' : 'var(--text-muted)' }}>
                        {c.label}{sort.key === c.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u, i) => (
                    <tr key={u.id} onClick={() => openDetail(u)}
                      style={{
                        borderBottom: '1px solid var(--border-light)',
                        background: i % 2 === 0 ? 'var(--card-bg)' : 'var(--hover-bg)',
                        opacity: u.isActive ? 1 : 0.55, cursor: 'pointer',
                      }}>
                      <td style={td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontWeight: 600 }}>{u.name}</span>
                          {u.role === 'admin' && <Badge label="admin" color="#F59E0B" />}
                          {!u.emailVerified && <Badge label="unverified" color="#EF4444" />}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>{u.email}</div>
                      </td>
                      <td style={td}>
                        <Badge label={u.plan} color={PLAN_COLORS[u.plan] || '#94a3b8'} />
                        <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 3 }}>{u.subscriptionStatus}</div>
                      </td>
                      <td style={tdR}>{num(u.documents)}</td>
                      <td style={tdR}>{num(u.clients)}</td>
                      <td style={tdR}>{num(u.items)}</td>
                      <td style={tdR}>{fmtMoney(u.totalValue, u.primaryCurrency)}</td>
                      <td style={{ ...tdR, color: 'var(--success)' }}>{fmtMoney(u.paidValue, u.primaryCurrency)}</td>
                      <td style={tdR}>{num(u.events30d)}</td>
                      <td style={tdR}>{num(u.logins)}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 12, color: 'var(--text-muted)' }}>{fmtAgo(u.lastActivityAt)}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 12, color: 'var(--text-muted)' }}>{fmtDate(u.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )
      }

      {/* Per-user drill-down */}
      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail?.user?.name || detail?.name || 'User'} width={760}>
        {detailLoading || detail?.loading
          ? <Spinner />
          : detail && <UserDetail d={detail} />
        }
      </Modal>
    </div>
  );
}

function UserDetail({ d }) {
  const cur = d.company?.currency || 'MYR';
  const maxMonth = Math.max(1, ...d.documents.byMonth.map(m => m.count));
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <Badge label={d.user.role} color={d.user.role === 'admin' ? '#F59E0B' : '#64748b'} />
        <Badge label={d.user.isActive ? 'active' : 'deactivated'} color={d.user.isActive ? '#10B981' : '#EF4444'} />
        <Badge label={d.user.emailVerified ? 'verified' : 'unverified'} color={d.user.emailVerified ? '#10B981' : '#F59E0B'} />
        <Badge label={d.user.plan} color={PLAN_COLORS[d.user.plan] || '#94a3b8'} />
        <Badge label={d.user.subscriptionStatus} color={STATUS_COLORS[d.user.subscriptionStatus] || '#94a3b8'} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 16 }}>
        <Card>
          <h3 style={cardH}>Account</h3>
          <Row k="Email"        v={d.user.email} />
          <Row k="Company"      v={d.company?.name || '—'} />
          <Row k="Joined"       v={fmtDate(d.user.createdAt)} />
          <Row k="Trial ends"   v={fmtDate(d.user.trialEndsAt)} />
          <Row k="Renews"       v={fmtDate(d.user.currentPeriodEnd)} />
          <Row k="Stripe ID"    v={d.user.stripeCustomerId || '—'} />
        </Card>

        <Card>
          <h3 style={cardH}>Engagement</h3>
          <Row k="Logins"          v={num(d.activity.logins)} />
          <Row k="First sign-in"   v={fmtDateTime(d.activity.firstLoginAt)} />
          <Row k="Last sign-in"    v={fmtDateTime(d.activity.lastLoginAt)} />
          <Row k="Total events"    v={num(d.activity.events)} />
          <Row k="Events (30d)"    v={num(d.activity.events30d)} />
          <Row k="Failed actions"  v={num(d.activity.failures)} />
          <Row k="Last active"     v={fmtAgo(d.activity.lastAt)} />
        </Card>

        <Card>
          <h3 style={cardH}>Documents</h3>
          <Row k="Total"          v={num(d.documents.count)} />
          <Row k="Total value"    v={fmtMoney(d.documents.totalValue, cur)} />
          <Row k="Paid value"     v={fmtMoney(d.documents.paidValue, cur)} />
          <Row k="Average value"  v={fmtMoney(d.documents.averageValue, cur)} />
          <Row k="Largest"        v={fmtMoney(d.documents.largestValue, cur)} />
          <Row k="Clients"        v={num(d.clients)} />
          <Row k="Saved items"    v={num(d.items)} />
        </Card>

        <Card>
          <h3 style={cardH}>By Type</h3>
          {d.documents.byType.length === 0
            ? <p style={muted}>No documents</p>
            : d.documents.byType.map(t => <Row key={t.key} k={t.key} v={`${num(t.count)} · ${fmtMoney(t.total, cur)}`} />)}
        </Card>

        <Card>
          <h3 style={cardH}>By Status</h3>
          {d.documents.byStatus.length === 0
            ? <p style={muted}>No documents</p>
            : d.documents.byStatus.map(s => <Row key={s.key} k={s.key} v={`${num(s.count)} · ${fmtMoney(s.total, cur)}`} />)}
        </Card>

        <Card>
          <h3 style={cardH}>Top Clients</h3>
          {d.topClients.length === 0
            ? <p style={muted}>No client data</p>
            : d.topClients.map(c => <Row key={c.name} k={c.name} v={`${num(c.count)} · ${fmtMoney(c.total, cur)}`} />)}
        </Card>
      </div>

      {d.documents.byMonth.length > 0 && (
        <Card style={{ marginTop: 16 }}>
          <h3 style={cardH}>Documents per month</h3>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 100 }}>
            {d.documents.byMonth.map(m => (
              <div key={m.month} title={`${m.month}: ${m.count} doc(s) · ${fmtMoney(m.total, cur)}`}
                style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, height: '100%', justifyContent: 'flex-end' }}>
                <div style={{
                  width: '100%', maxWidth: 34, borderRadius: '4px 4px 0 0',
                  height: `${Math.max(4, (m.count / maxMonth) * 100)}%`,
                  background: 'linear-gradient(180deg, #10B981, #047857)',
                }} />
                <span style={{ fontSize: 9, color: 'var(--text-subtle)', whiteSpace: 'nowrap' }}>{m.month.slice(2)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card style={{ marginTop: 16 }}>
        <h3 style={cardH}>Most used actions</h3>
        {d.activity.topActions.length === 0
          ? <p style={muted}>No recorded activity</p>
          : d.activity.topActions.map(a => <Row key={a.action} k={a.action} v={`${num(a.count)} · last ${fmtAgo(a.lastAt)}`} />)}
      </Card>

      <Card style={{ marginTop: 16 }}>
        <h3 style={cardH}>Recent activity</h3>
        {d.activity.recent.length === 0
          ? <p style={muted}>No recorded activity</p>
          : d.activity.recent.map(e => (
              <div key={e._id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 0', borderBottom: '1px solid var(--border-light)' }}>
                <span style={{ fontSize: 13, color: e.success ? 'var(--text)' : 'var(--danger)' }}>{e.description}</span>
                <span style={{ fontSize: 11, color: 'var(--text-subtle)', whiteSpace: 'nowrap' }}>{fmtDateTime(e.createdAt)}</span>
              </div>
            ))}
      </Card>
    </div>
  );
}

function DistBar({ rows, total, colors }) {
  if (!rows.length) return <p style={muted}>No data</p>;
  return (
    <div>
      <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', marginBottom: 14 }}>
        {rows.map(r => (
          <div key={r.key} title={`${r.key}: ${r.count}`}
            style={{ width: `${total ? (r.count / total) * 100 : 0}%`, background: colors[r.key] || '#94a3b8' }} />
        ))}
      </div>
      {rows.map(r => (
        <div key={r.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, padding: '5px 0' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)' }}>
            <span style={{ width: 9, height: 9, borderRadius: 3, background: colors[r.key] || '#94a3b8', display: 'inline-block' }} />
            {r.key}
          </span>
          <span style={{ fontWeight: 500, color: 'var(--text)' }}>
            {num(r.count)} <span style={{ color: 'var(--text-subtle)', fontWeight: 400 }}>({total ? Math.round(r.count / total * 100) : 0}%)</span>
          </span>
        </div>
      ))}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, padding: '6px 0' }}>
      <span style={{ color: 'var(--text-muted)' }}>{k}</span>
      <span style={{ fontWeight: 500, color: 'var(--text)', textAlign: 'right', wordBreak: 'break-word' }}>{v}</span>
    </div>
  );
}

const cardH = { fontSize: 14, fontWeight: 600, color: 'var(--heading)', marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid var(--border-light)' };
const muted = { fontSize: 13, color: 'var(--text-subtle)' };
const th = { padding: '12px 14px', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', userSelect: 'none' };
const td = { padding: '12px 14px', fontSize: 14, color: 'var(--text)', verticalAlign: 'middle' };
const tdR = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };
